import React, { useCallback, useMemo, useRef, useState } from 'react'
import { useVirtualizer } from '@tanstack/react-virtual'
import {
	ChevronRight,
	Close,
	Done,
	MoreHoriz,
	Pending,
	Warning,
} from '@mui/icons-material'
import cn from '../../../../lib/utils/cn'
import FileFormatIcon from '../../../common/FileFormatIcon'
import { Tooltip } from '../../../ui'
import type { UploadFileStatus } from '../../../../context/progressContext'
import {
	getFolderUploadOutcomes,
	getVisibleUploadRows,
	isUploadActive,
	type UploadFilter,
	type UploadStatus,
	type UploadVisibleRow,
} from '../../../../lib/utils/upload'
interface UploadTreeListProps {
	items: UploadFileStatus[]
	filter: UploadFilter
	renderActions: (item: UploadFileStatus) => React.ReactNode
}

const StatusIcon = ({
	status,
	className,
	isWarning = false,
}: {
	status: UploadStatus
	className?: string
	isWarning?: boolean
}) => {
	const iconClass = cn('fill-white', className ?? 'w-4 h-4')

	if (isWarning) {
		return <Warning className={iconClass} />
	}
	if (status === 'completed') {
		return <Done className={iconClass} />
	}
	if (status === 'failed' || status === 'canceled') {
		return <Close className={iconClass} />
	}
	if (status === 'uploading') {
		return <MoreHoriz className={iconClass} />
	}
	return <Pending className={cn(iconClass, 'animate-pulse')} />
}

const getItemSurfaceClass = (depth: number) =>
	depth % 2 === 0
		? 'bg-background-light dark:bg-background-dark'
		: 'bg-background-light-hover dark:bg-background-dark-light'

const StatusBadge = ({
	status,
	depth,
	isFolder,
	isWarning = false,
	title,
}: {
	status: UploadStatus
	depth: number
	isFolder: boolean
	isWarning?: boolean
	title?: string
}) => (
	<div
		title={title}
		className={cn(
			'absolute z-10 w-3.5 h-3.5 rounded-full flex items-center justify-center ring-2',
			isFolder ? ' -top-1 -right-2.5' : '-top-1 -right-1',
			depth % 2 === 0
				? 'ring-background-light dark:ring-background-dark'
				: 'ring-background-light-hover dark:ring-background-dark-light',
			isWarning
				? 'bg-accent'
				: status === 'completed'
					? 'bg-positive'
					: status === 'failed'
						? 'bg-negative'
						: status === 'canceled' || status === 'uploading'
							? 'bg-text-secondary'
							: 'bg-accent',
		)}
	>
		<StatusIcon status={status} isWarning={isWarning} className="w-2.5 h-2.5" />
	</div>
)

const UploadTreeList: React.FC<UploadTreeListProps> = ({
	items,
	filter,
	renderActions,
}) => {
	const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set())
	const [collapsedIds, setCollapsedIds] = useState<Set<string>>(new Set())
	const scrollElementRef = useRef<HTMLDivElement>(null)

	const folderOutcomes = useMemo(() => getFolderUploadOutcomes(items), [items])

	const visibleRows = useMemo(
		() =>
			getVisibleUploadRows({
				items,
				filter,
				expandedIds,
				collapsedIds,
			}),
		[items, filter, expandedIds, collapsedIds],
	)
	const getRowKey = useCallback(
		(index: number) => visibleRows[index]?.item.id || index,
		[visibleRows],
	)
	const rowVirtualizer = useVirtualizer({
		count: visibleRows.length,
		getScrollElement: () => scrollElementRef.current,
		estimateSize: () => 48,
		getItemKey: getRowKey,
		overscan: 8,
		useFlushSync: false,
	})

	const renderRow = (row: UploadVisibleRow): React.ReactNode => {
		const { item, depth, hasChildren, isExpanded } = row
		const isFolder = item.nodeType === 'folder'

		if (
			!item.id
		) return null

		const displayName =
			item.folderName || item.originalFileName || item.file?.name || 'Untitled'
		const error = item.error
		const folderOutcome =
			isFolder && item.id ? folderOutcomes.get(item.id) : undefined
		const isFolderWarning = filter === 'all' && folderOutcome?.isWarning
		const displayStatus =
			isFolder && filter === 'all'
				? folderOutcome?.status || item.status
				: isFolder && filter !== 'all'
					? filter
					: item.status
		const folderOutcomeLabel =
			isFolder && filter === 'all' ? folderOutcome?.label : undefined

		return (
			<div
				className={cn('w-full min-w-0', depth === 0 ? 'pb-2' : 'pb-1')}
			>
				<div
					className={cn(
						'group w-auto min-w-0 min-h-10 py-1 pr-2 flex items-center gap-0',
						'rounded-md',
						getItemSurfaceClass(depth),
					)}
					style={{ paddingLeft: `${8 + depth}px` }}
				>
					{isFolder && hasChildren ? (
						<div className="w-6 shrink-0 flex justify-center">
							<button
								type="button"
								onClick={() => {
									setExpandedIds((previousIds) => {
										const nextIds = new Set(previousIds)
										isExpanded
											? nextIds.delete(item.id!)
											: nextIds.add(item.id!)
										return nextIds
									})
									setCollapsedIds((previousIds) => {
										const nextIds = new Set(previousIds)
										isExpanded
											? nextIds.add(item.id!)
											: nextIds.delete(item.id!)
										return nextIds
									})
								}}
								className="w-6 h-6 p-0 border-0 bg-transparent inline-flex items-center justify-center text-text-secondary dark:text-text-main-dark hover:bg-background-light-hover dark:hover:bg-background-dark-hover rounded"
								aria-label={isExpanded ? 'Collapse folder' : 'Expand folder'}
							>
								<ChevronRight
									className={cn(
										'w-4 h-4 transition-transform',
										isExpanded && 'rotate-90',
									)}
								/>
							</button>
						</div>
					) : null}
					<div className="min-w-0 flex-1 ml-2">
						<div className="flex items-center gap-3.5 min-w-0">
							{error && !isUploadActive(item.status) ? (
								<Tooltip
									label={error}
									classNameChildrenWrapper="overflow-visible shrink-0 w-5 h-5"
								>
									<div className="relative shrink-0 w-5 h-5">
										<FileFormatIcon
											fileName={displayName}
											isFolder={isFolder}
											size={20}
										/>
										<StatusBadge
											status={displayStatus}
											depth={depth}
											isFolder={isFolder}
											isWarning={isFolderWarning}
											title={folderOutcomeLabel}
										/>
									</div>
								</Tooltip>
							) : (
								<div className="relative shrink-0 w-5 h-5">
									<FileFormatIcon
										fileName={displayName}
										isFolder={isFolder}
										size={20}
									/>
									<StatusBadge
										status={displayStatus}
										depth={depth}
										isFolder={isFolder}
										isWarning={isFolderWarning}
										title={folderOutcomeLabel}
									/>
								</div>
							)}
							<span
								className="block flex-1 min-w-0 w-full text-sm text-text-main dark:text-text-main-dark truncate"
								style={
									{
										// paddingLeft: `${8 + depth * 14}px`
										// width:
									}
								}
								title={displayName}
							>
								{displayName}
							</span>
						</div>
						{isUploadActive(displayStatus) && (
							<div className="h-1 mt-1.5 bg-background-light-hover dark:bg-background-dark-hover rounded overflow-hidden">
								<div
									className="h-full bg-accent rounded transition-[width] duration-200"
									style={{
										width: `${item.progress}%`,
									}}
								/>
							</div>
						)}
					</div>
					<div className="ml-1.5 shrink-0 flex items-center">
						{renderActions(item)}
					</div>
				</div>
			</div>
		)
	}

	return (
		<div
			ref={scrollElementRef}
			className="flex flex-1 mt-3 w-full min-w-0 min-h-40 max-h-none overflow-y-auto overflow-x-hidden pr-1"
		>
			{visibleRows.length > 0 ? (
				<div
					className="relative w-full min-w-0"
					style={{ height: `${rowVirtualizer.getTotalSize()}px` }}
				>
					{rowVirtualizer.getVirtualItems().map((virtualRow) => (
						<div
							key={virtualRow.key}
							ref={rowVirtualizer.measureElement}
							data-index={virtualRow.index}
							className="absolute left-0 top-0 w-full min-w-0"
							style={{ transform: `translateY(${virtualRow.start}px)` }}
						>
							{renderRow(visibleRows[virtualRow.index])}
						</div>
					))}
				</div>
			) : (
				<div className="flex flex-1 min-h-40 items-center justify-center">
					<p className="text-text-secondary text-xs">No items</p>
				</div>
			)}
		</div>
	)
}

export default UploadTreeList
